import { getZrmListingInfo, getZrmUserInfo } from '@zg-rentals/clickstream';

import {
  RELISTING_STEP_SUFFIX,
  STEP_DELIMITER,
  STEP_KEYS,
} from '../../components/CreateListingPage/constants/stepKeys';

import { trackEvent } from '../index';

import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

export const EVENT_IDS = {
  CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE: '4549',
  CLICK_UPGRADE_TO_PREMIUM_FROM_HEADER: '7497',
  CLICK_UPGRADE_TO_PREMIUM_FROM_MODAL: '8293',
  COMPLETE_UPGRADE_TO_PREMIUM: '8294',
  CONTINUE_FROM: '3402',
  DISABLE_APPS: '3404',
  ENABLE_APPS: '3403',
  NEXT: '3401',
  SAVE_AND_EXIT: '3400',
} as const;

const TRIGGER_SOURCES = {
  PREACTIVATION: 'zrm_premium_confirm_upgrade_preactivation',
  AT_ACTIVATION: 'zrm_premium_confirm_upgrade_at_activation',
};

export type CompleteUpgradeToPremiumTriggerSource =
  | typeof TRIGGER_SOURCES.PREACTIVATION
  | typeof TRIGGER_SOURCES.AT_ACTIVATION;

const getFormattedStepKey = (stepKey: string) => {
  let formattedStepKey = stepKey;

  formattedStepKey = formattedStepKey.toLowerCase();

  // handle dynamically generated steps
  if (formattedStepKey === `${STEP_KEYS.REVIEW}${STEP_DELIMITER}${RELISTING_STEP_SUFFIX}`) {
    formattedStepKey = `${STEP_KEYS.REVIEW}_${RELISTING_STEP_SUFFIX}`;
  }

  return formattedStepKey;
};

// https://dataportal.zillowgroup.net/event-registry/7497
export const emitClickUpgradeToPremiumFromHeaderEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CLICK_UPGRADE_TO_PREMIUM_FROM_HEADER,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'upgrade_listing_to_premium',
      trigger_object_nm: 'modular_listing_header',
    },
    semantic: {
      semantic_event_nm: 'start_upgrade_to_premium',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8293
export const emitClickUpgradeToPremiumFromModalEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CLICK_UPGRADE_TO_PREMIUM_FROM_MODAL,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'upgrade_listing_to_premium',
      trigger_object_nm: 'zrm_premium_info_modal',
    },
    semantic: {
      semantic_event_nm: 'zrm_premium_early_upgrade',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/8294
export const emitCompleteUpgradeToPremiumEvent = ({
  currentProperty,
  user,
  triggerSource,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerSource: CompleteUpgradeToPremiumTriggerSource;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.COMPLETE_UPGRADE_TO_PREMIUM,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: triggerSource,
      trigger_object_nm: 'zrm_premium_checkout_modal',
    },
    semantic: {
      semantic_event_nm: 'zrm_premium_checkout',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3400
export const emitSaveAndExitEvent = ({
  currentProperty,
  user,
  currentStepKey,
}: {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  currentStepKey: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.SAVE_AND_EXIT,
      event_type_version_id: '3',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'save_and_exit',
      trigger_object_nm: getFormattedStepKey(currentStepKey),
    },
    semantic: {
      semantic_event_nm: 'exit_modular_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3401
export const emitNextEvent = ({
  currentProperty,
  user,
  currentStepKey,
}: {
  currentProperty?: ZrmClickstreamListing;
  user: ZrmClickstreamUser | null;
  currentStepKey: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.NEXT,
      event_type_version_id: '7',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'next',
      trigger_object_nm: getFormattedStepKey(currentStepKey),
    },
    semantic: {
      semantic_event_nm: 'continue_modular_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3402
export const emitContinueFromEvent = ({
  currentProperty,
  user,
  currentStepKey,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  currentStepKey: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CONTINUE_FROM,
      event_type_version_id: '2',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'resume',
      trigger_object_nm: getFormattedStepKey(currentStepKey),
    },
    semantic: {
      semantic_event_nm: 'continue_modular_listing',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3403
export const emitApplicationsEnableFromDisabledEvent = ({
  currentProperty,
  user,
  triggerLocation = 'posting_path',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.ENABLE_APPS,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'yes_most_common',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'enable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/3404
export const emitApplicationsDisableFromEnabledEvent = ({
  currentProperty,
  user,
  triggerLocation = 'posting_path',
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
  triggerLocation?: string;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.DISABLE_APPS,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: triggerLocation,
      trigger_source_nm: 'no',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'disable_applications',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });

// https://dataportal.zillowgroup.net/event-registry/4549
export const emitClickSampleApplicationLinkOnPageEvent = ({
  currentProperty,
  user,
}: {
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}) =>
  trackEvent.listingUser({
    envelope: {
      event_type_id: EVENT_IDS.CLICK_SAMPLE_APPLICATION_LINK_ON_PAGE,
      event_type_version_id: '1',
      event_template_id: '168',
      event_template_version_id: '1',
    },
    clickstream_trigger: {
      trigger_type_nm: 'interaction',
      trigger_location_nm: 'posting_path',
      trigger_source_nm: 'sample_application',
      trigger_object_nm: 'no_trigger_object',
    },
    semantic: {
      semantic_event_nm: 'view_sample_application',
      topic_tag_txt: ['zrm_properties'],
    },
    ...getZrmListingInfo(currentProperty),
    ...getZrmUserInfo(user),
  });
